import { useQuery } from '@tanstack/react-query';
import { requireSupabase, isSupabaseConfigured } from '@/lib/supabase/client';
import type { SearchResult, SearchResultItem } from '@/lib/supabase/database.types';
import { criteriaToRpcParams, type SearchCriteria } from './criteria';

/**
 * Recherche du catalogue.
 *
 * Un seul appel RPC renvoie la page courante ET le total : la pagination est
 * faite en base, on ne charge jamais toute la flotte dans le navigateur.
 * Le filtre de disponibilité est appliqué côté serveur, à partir des
 * réservations actives et des blocages saisis par les agences.
 */
export function useVehicleSearch(criteria: SearchCriteria) {
  return useQuery({
    queryKey: ['search', criteriaToRpcParams(criteria)],
    enabled: isSupabaseConfigured,
    staleTime: 30_000,
    queryFn: async (): Promise<SearchResult> => {
      const { data, error } = await requireSupabase().rpc('search_vehicles', {
        p_params: criteriaToRpcParams(criteria) as never,
      });
      if (error) throw new Error(error.message);
      return data as unknown as SearchResult;
    },
  });
}

/** Véhicules mis en avant, utilisés sur la page d'accueil. */
export function useFeaturedVehicles(city?: string, limit = 6) {
  return useQuery({
    queryKey: ['search', 'featured', city, limit],
    enabled: isSupabaseConfigured,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<SearchResultItem[]> => {
      const { data, error } = await requireSupabase().rpc('search_vehicles', {
        p_params: { city: city ?? null, sort: 'popular', limit } as never,
      });
      if (error) throw new Error(error.message);
      return (data as unknown as SearchResult).items;
    },
  });
}

/** Marques présentes au catalogue, pour alimenter le filtre « Marque ». */
export function useAvailableBrands(city?: string) {
  return useQuery({
    queryKey: ['search', 'brands', city],
    enabled: isSupabaseConfigured,
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<string[]> => {
      let query = requireSupabase().from('vehicles').select('brand').eq('status', 'published');
      if (city) {
        const { data: cityRow } = await requireSupabase()
          .from('cities')
          .select('id')
          .eq('slug', city)
          .maybeSingle();
        if (cityRow) query = query.eq('city_id', cityRow.id);
      }
      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return [...new Set((data ?? []).map((row) => row.brand))].sort((a, b) => a.localeCompare(b));
    },
  });
}
